import './global';
import { postEventLoggerApiCall } from '../features/event-logger/event-logger.apis';
import store from '../store/store';
import { StoreState } from '../store/store.interface';
import { getCustomerId } from '../features/customer/customer';
import { getQuoteIdFromCartProducts } from '../features/cart/shop-cart/store/shop-cart.selectors';

/**
 * The actions available to be passed into recaptcha. The Shop-side validate
 * endpoint (/store/api/v1/checkout/security/captcha/validate) has been
 * removed under [SHOP-API-REMOVAL][WECOMM-20796]; tokens are now forwarded to
 * Magento via the X-ReCaptcha header (see MAGENTO_RECAPTCHA_HEADER).
 */
export enum RecaptchaAction {
  payment = 'PAYMENT',
  cart = 'CART',
  sample = 'SAMPLE',
}

type RecaptchaConfigType = {
  enabled: boolean;
  configKey?: string;
};

/**
 * @returns The recaptcha site key as found in the recaptcha script tag.
 *          Or undefined if no key is found.
 */
function getShopRecaptchaClientId() {
  return document.querySelector('[data-recaptcha-site-key]')?.getAttribute('data-recaptcha-site-key');
}

/**
 * @returns a recaptcha config object with properties enabled and configKey
 */
const getRecaptchaConfig: () => RecaptchaConfigType = (() => {
  let recaptchaConfig: RecaptchaConfigType | undefined;

  return (): RecaptchaConfigType => {
    // Cache only successful discovery, so if the key is temporarily unavailable we can recover.
    if (recaptchaConfig?.enabled) return recaptchaConfig;

    const googleRecaptchaClientId = getShopRecaptchaClientId();
    if (googleRecaptchaClientId) {
      recaptchaConfig = {
        enabled: true,
        configKey: googleRecaptchaClientId,
      };
      return recaptchaConfig;
    }

    return { enabled: false };
  };
})();

/**
 * Best-effort readiness for grecaptcha.enterprise.
 * Keeps behavior consistent: if grecaptcha isn't present yet, we resolve and token gen will return undefined.
 */
async function recaptchReady(): Promise<void> {
  const recaptchaConfig = getRecaptchaConfig();
  if (!recaptchaConfig.enabled) return;

  return new Promise((resolve) => {
    if (window.grecaptcha?.enterprise?.ready) {
      window.grecaptcha.enterprise.ready(() => resolve());
      return;
    }
    resolve();
  });
}

type CaptchaFailurePayload = {
  stage:
    | 'missing-site-key'
    | 'recaptcha-script-load-failed'
    | 'grecaptcha-not-loaded'
    | 'execute-returned-empty'
    | 'execute-threw';
  action: RecaptchaAction;

  // optional diagnostics
  error?: string;
  durationMs?: number;
  isRecaptchaLoaded?: boolean;
};

/**
 * Safe stringify for errors (so event-logger payload doesn't explode on circular objects).
 */
function stringifyError(err: unknown): string {
  try {
    if (err instanceof Error) {
      return `${err.name}: ${err.message}\n${err.stack ?? ''}`.trim();
    }
    return typeof err === 'string' ? err : JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * Better, generic error logger (no Turnstile assumptions).
 */
async function logCaptchaFailure(details: CaptchaFailurePayload) {
  try {
    const storeState = store.getState() as StoreState;
    const customerId = getCustomerId(storeState);
    const orderId = 'RECAPTCHA_PLACEHOLDER_ORDER_ID';

    postEventLoggerApiCall({
      uiDump: {
        eventName: 'recaptcha-captcha-token-generation-failed',
        orderId,
        rmsOrderId: orderId,
        message: JSON.stringify({
          isGuestCheckout: !customerId,
          ...(customerId && { customerId }),
          ...(!customerId && { quoteId: getQuoteIdFromCartProducts(storeState) }),
          ...details,
        }),
      },
    });
  } catch (e) {
    console.error('Failed to log recaptcha failure to event logger');
  }
}

/**
 * @returns A promise that resolves to a string (gRecaptcha enterprise token) or undefined.
 */
async function getGrecaptchaToken(action: RecaptchaAction): Promise<string | undefined> {
  const recaptchaConfig = getRecaptchaConfig();
  if (!recaptchaConfig.enabled) return undefined;

  const recaptchaClientId = recaptchaConfig.configKey;

  if (!recaptchaClientId) {
    await logCaptchaFailure({
      stage: 'missing-site-key',
      action,
    });
    return undefined;
  }

  if (!window.grecaptcha) {
    await logCaptchaFailure({
      stage: 'recaptcha-script-load-failed',
      action,
      isRecaptchaLoaded: false,
    });
    return undefined;
  }

  if (!window.grecaptcha?.enterprise?.execute) {
    await logCaptchaFailure({
      stage: 'grecaptcha-not-loaded',
      action,
      isRecaptchaLoaded: !!window.grecaptcha,
    });
    return undefined;
  }

  try {
    await recaptchReady();

    const start = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();

    const token: any = await window.grecaptcha.enterprise.execute(recaptchaClientId, { action });

    const end = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();

    if (!token) {
      await logCaptchaFailure({
        stage: 'execute-returned-empty',
        action,
        durationMs: Math.round((end as number) - (start as number)),
      });
      return undefined;
    }

    return token;
  } catch (error) {
    await logCaptchaFailure({
      stage: 'execute-threw',
      action,
      error: stringifyError(error),
    });
    return undefined;
  }
}

/**
 *
 * @param action The type of action that you need to validate. The available options are
 *               listed by the RecaptchaAction enum.
 * @returns A promise that resolves to a string (recaptcha token) or undefined.
 */
export async function getRecaptchaToken(action: RecaptchaAction): Promise<string | undefined> {
  const recaptchaConfig = getRecaptchaConfig();
  if (!recaptchaConfig.enabled) return undefined;

  return await getGrecaptchaToken(action);
}

// [SHOP-API-REMOVAL][WECOMM-20796] `validateRecaptcha()` and its private
// `validate()` helper (which POSTed to the Shop endpoint
// /store/api/v1/checkout/security/captcha/validate) have been removed. They
// had no callers in source or tests. Magento now performs reCAPTCHA validation
// server-side using the token forwarded via the `X-ReCaptcha` header
// (MAGENTO_RECAPTCHA_HEADER). Callers should use `getRecaptchaToken()` only.
